import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "@/context/LangContext";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const DAY_IDX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const HOMEPAGE_HORIZON_DAYS = 30;

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function isoPlusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function parseTimeRange(range) {
  // "16:00 - 17:30" or "16:00-17:30"
  const m = (range || "").match(/(\d{1,2}):(\d{2})\s*[-–~]\s*(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return {
    startMin: parseInt(m[1], 10) * 60 + parseInt(m[2], 10),
    endMin: parseInt(m[3], 10) * 60 + parseInt(m[4], 10),
  };
}

function beijingNow() {
  // Build a Date-like view of Asia/Shanghai using Intl
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Shanghai", weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = fmt.formatToParts(new Date());
  const get = (k) => parts.find(p => p.type === k)?.value;
  const weekday = get("weekday"); // e.g. "Sat"
  const hour = parseInt(get("hour"), 10);
  const minute = parseInt(get("minute"), 10);
  return { weekday, minutes: hour * 60 + minute };
}

function statusFor(schedule, now, isOffDay) {
  if (schedule.status === "suspended") return "red";
  if (isOffDay) return "off";
  if (schedule.status === "planned") return "blue";
  const days = (schedule.days_of_week && schedule.days_of_week.length) ? schedule.days_of_week : (schedule.day_of_week ? [schedule.day_of_week] : []);
  if (!days.length || !schedule.time_slot) return null;
  if (!days.includes(now.weekday)) return "gray";
  const tr = parseTimeRange(schedule.time_slot);
  if (!tr) return null;
  if (now.minutes >= tr.startMin && now.minutes <= tr.endMin) return "green";
  if (tr.startMin - now.minutes > 0 && tr.startMin - now.minutes <= 60) return "yellow";
  return null;
}

const TONES = {
  green: { dot: "bg-emerald-500", ring: "ring-emerald-500/30", label_en: "Running now", label_zh: "正在上课" },
  yellow: { dot: "bg-amber-500", ring: "ring-amber-500/30", label_en: "Starts within 1h", label_zh: "1 小时内开始" },
  red: { dot: "bg-red-500", ring: "ring-red-500/30", label_en: "Suspended", label_zh: "已停课" },
  blue: { dot: "bg-sky-500", ring: "ring-sky-500/30", label_en: "Opening soon", label_zh: "即将开课" },
  gray: { dot: "bg-stone-400", ring: "ring-stone-400/25", label_en: "No class today", label_zh: "今日无课" },
  off: { dot: "bg-rose-400", ring: "ring-rose-400/30", label_en: "Studio closed", label_zh: "工作室休息" },
};

function formatStartDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    if (lang === "zh") return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function SchedulesSection() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [offToday, setOffToday] = useState(null); // null | {title, title_zh}
  const [tick, setTick] = useState(0);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/schedules"); setItems(data); } catch (e) { /* noop */ }
  })(); }, []);
  useEffect(() => { (async () => {
    try {
      const today = todayISO();
      const { data } = await api.get(`/calendar?start=${today}&end=${today}`);
      const events = data?.events || [];
      const holidays = data?.holidays || [];
      const off = events.find(e => e.type === "off_day" && (e.date <= today) && ((e.end_date || e.date) >= today))
                || holidays.find(h => h.date === today);
      setOffToday(off || null);
    } catch (_e) { /* noop */ }
  })(); }, []);
  // Re-evaluate the light status every minute
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const now = beijingNow();
  const visibleItems = useMemo(() => {
    const today = todayISO();
    const horizon = isoPlusDays(HOMEPAGE_HORIZON_DAYS);
    return (items || []).filter((s) => {
      // Class has ended → hide.
      if (s.end_date && s.end_date < today) return false;
      // Class starts further than a month from now → hide until it gets closer.
      if (s.start_date && s.start_date > horizon) return false;
      return true;
    });
  }, [items]);

  return (
    <section id="schedules" data-testid="schedules-section" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-[hsl(var(--border))]">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.25em] text-[hsl(var(--muted-foreground))]">{t.schedules.eyebrow}</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium mt-3">{t.schedules.title}</h2>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-md">{t.schedules.sub}</p>
          <ul className="flex flex-wrap items-center gap-4 text-[11px] text-[hsl(var(--muted-foreground))]" data-testid="schedules-light-legend">
            <li className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />{lang==="zh"?"正在上课":"Running now"}</li>
            <li className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" />{lang==="zh"?"1 小时内":"Within 1h"}</li>
            <li className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-sky-500" />{lang==="zh"?"即将开课":"Opening soon"}</li>
            <li className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-stone-400" />{lang==="zh"?"今日无课":"No class today"}</li>
            <li className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-rose-400" />{lang==="zh"?"工作室休息":"Studio closed"}</li>
            <li className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" />{lang==="zh"?"已停课":"Suspended"}</li>
          </ul>
        </div>
      </div>

      <div className="mt-10 overflow-x-auto">
        {offToday && (
          <div data-testid="schedules-studio-closed-banner" className="mb-4 rounded-2xl border border-rose-300/60 bg-rose-50/80 px-5 py-3 text-sm text-rose-900 flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-400 ring-4 ring-rose-200" />
            <div>
              <span className="font-display tracking-tight">{lang === "zh" ? "今日工作室休息" : "Studio is closed today"}</span>
              <span className="text-rose-700/80"> · {lang === "zh" && offToday.title_zh ? offToday.title_zh : offToday.title}</span>
            </div>
          </div>
        )}
        <table className="w-full text-sm border-collapse" data-testid="schedules-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
              <th className="py-3 font-normal w-10"></th>
              <th className="py-3 font-normal">{t.schedules.th_class}</th>
              <th className="py-3 font-normal">{t.schedules.th_program}</th>
              <th className="py-3 font-normal">{t.schedules.th_teacher}</th>
              <th className="py-3 font-normal">{t.schedules.th_day}</th>
              <th className="py-3 font-normal">{t.schedules.th_time}</th>
              <th className="py-3 font-normal">{t.schedules.th_level}</th>
              <th className="py-3 font-normal text-right">{t.schedules.th_seats}</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((s) => {
              const left = Math.max(0, (s.seats_total||0) - (s.seats_taken||0));
              const isPlanned = s.status === "planned";
              const isOpen = left > 0 && s.status !== "suspended";
              const light = statusFor(s, now, !!offToday);
              const tone = light ? TONES[light] : null;
              const lightLabel = tone ? (lang === "zh" ? tone.label_zh : tone.label_en) : "";
              const startLabel = formatStartDate(s.start_date, lang);
              return (
                <tr key={s.id + "_" + tick} data-testid={`schedule-row-${s.id}`} className="border-t border-[hsl(var(--border))]/70 hover:bg-[hsl(var(--muted))]/50 transition-colors">
                  <td className="py-4">
                    {tone ? (
                      <span title={lightLabel} data-testid={`schedule-light-${s.id}`} className={`inline-block w-3 h-3 rounded-full ${tone.dot} ring-4 ${tone.ring}`}></span>
                    ) : (
                      <span className="inline-block w-3 h-3 rounded-full bg-[hsl(var(--border))]" />
                    )}
                  </td>
                  <td className="py-4 font-medium">
                    {lang === "zh" && s.name_zh ? s.name_zh : s.name}
                    {tone && <span className={`ml-2 text-[10px] uppercase tracking-[0.15em] ${light==="green"?"text-emerald-600":light==="yellow"?"text-amber-600":light==="blue"?"text-sky-600":light==="gray"?"text-stone-500":light==="off"?"text-rose-600":"text-red-600"}`}>· {lightLabel}</span>}
                    {isPlanned && startLabel && (
                      <div className="text-[11px] text-sky-700 mt-0.5 font-normal" data-testid={`schedule-start-date-${s.id}`}>
                        {lang === "zh" ? `开课日 · ${startLabel}` : `Starts · ${startLabel}`}
                      </div>
                    )}
                    {s.end_date && (
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 font-normal" data-testid={`schedule-end-date-${s.id}`}>
                        {lang === "zh" ? `截至 · ${formatStartDate(s.end_date, lang)}` : `Until · ${formatStartDate(s.end_date, lang)}`}
                      </div>
                    )}
                  </td>
                  <td className="py-4 text-[hsl(var(--muted-foreground))]">{s.program}</td>
                  <td className="py-4 text-[hsl(var(--muted-foreground))]">{s.teacher}</td>
                  <td className="py-4 text-[hsl(var(--muted-foreground))]">{(s.days_of_week && s.days_of_week.length) ? s.days_of_week.join(" · ") : s.day_of_week}</td>
                  <td className="py-4 text-[hsl(var(--muted-foreground))]">{s.time_slot}</td>
                  <td className="py-4 text-[hsl(var(--muted-foreground))]">{s.level}</td>
                  <td className="py-4 text-right">
                    {s.status === "suspended" ? (
                      <Badge variant="outline" data-testid={`schedule-seats-${s.id}`} className="border-red-500/40 text-red-600 bg-red-50">{lang==="zh"?"已停课":"Suspended"}</Badge>
                    ) : isPlanned ? (
                      <Badge variant="outline" data-testid={`schedule-seats-${s.id}`} className="border-sky-500/40 text-sky-700 bg-sky-50">
                        {left > 0
                          ? (lang === "zh" ? `${left} 名额预留` : `${left} reserve spots`)
                          : (lang === "zh" ? "等候名单" : "Waitlist")}
                      </Badge>
                    ) : isOpen ? (
                      <Badge variant="outline" data-testid={`schedule-seats-${s.id}`} className="border-[hsl(var(--secondary))] text-[hsl(var(--secondary))] bg-[hsl(var(--secondary))]/5">{left} {t.schedules.open}</Badge>
                    ) : (
                      <Badge variant="outline" data-testid={`schedule-seats-${s.id}`} className="border-[hsl(var(--muted-foreground))]/40 text-[hsl(var(--muted-foreground))]">{t.schedules.full}</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
            {visibleItems.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-[hsl(var(--muted-foreground))]">—</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
